import { useRef, useEffect, useMemo } from "react";
import { useMessagesStore } from "@/entities/message";
import { Message } from "@/entities/message";
import { debounce } from "lodash";
import { useScrollStore } from "./message-scroll";
import { useShallow } from "zustand/react/shallow";
import { useChatStore } from "@/entities/chat";

interface UseMessageReadObserverProps {
  chatId: string | undefined;
  messages: Message[];
  user: { _id: string } | null;
  messageRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  hasScrolled: boolean;
}

export const useMessageReadObserver = ({
  chatId,
  user,
  messageRefs,
  messages,
  chatContainerRef,
  hasScrolled,
}: UseMessageReadObserverProps) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const lastReadMessageIdRef = useRef<string | null>(null);
  const unreadCountRecord = useScrollStore((state) => state.unreadCount);

  const sendReadedDebounced = useRef(
    debounce((chatId: string, msgId: string, currentUserId: string) => {
      useMessagesStore
        .getState()
        .sendReadedMessages(chatId, msgId, currentUserId);
    }, 500)
  ).current;

  const messageIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    if (messages?.length) {
      messages.forEach((msg, i) => map.set(msg._id, i));
    }
    return map;
  }, [messages]);

  useEffect(() => {
    if (
      !hasScrolled ||
      !chatContainerRef.current ||
      !messages?.length ||
      !user ||
      !chatId
    ) {
      return;
    }

    const container = chatContainerRef.current;

    // Очищаем старый observer при пересоздании
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        const visibleMessageIds: string[] = [];

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const element = entry.target as HTMLElement;
          const messageId = element.getAttribute("data-msg-id");
          if (messageId) {
            visibleMessageIds.push(messageId);
          }
        });

        if (visibleMessageIds.length === 0) return;

        const visibleIncomingMessageIds = visibleMessageIds.filter((id) => {
          const msg = messages.find((m) => m._id === id);
          return msg && msg.owner._id !== user._id && !msg.readedAt && !msg._id.startsWith("temp");
        });


        if (visibleIncomingMessageIds.length === 0) return;

        const sortedVisibleIncomingIds = visibleIncomingMessageIds.sort(
          (a, b) => {
            return (
              (messageIndexMap.get(a) ?? 0) - (messageIndexMap.get(b) ?? 0)
            );
          }
        );

        const lastVisibleMessageId =
          sortedVisibleIncomingIds[sortedVisibleIncomingIds.length - 1];
        if (lastReadMessageIdRef.current === lastVisibleMessageId) return;

        const state = useMessagesStore.getState();
        const result = state.markMessagesAsRead(
          chatId,
          lastVisibleMessageId,
          user._id
        );

        if (!result) return;

        const { lastReadMessageId, unreadCount } = result;

        useScrollStore.getState().setUnreadCount(chatId, unreadCount);
        useChatStore.getState().updateUnreadCount(chatId, unreadCount);

        if (lastReadMessageId) {
          lastReadMessageIdRef.current = lastReadMessageId;
          sendReadedDebounced(chatId, lastReadMessageId, user._id);
        }
      },
      {
        root: container,
        threshold: 0.3,
      }
    );

    messages.forEach((msg) => {
      const el = messageRefs.current[msg._id];
      if(msg.readedAt) return;

      if (el) {
        observer.current?.observe(el);
      }
    });

    return () => {
      observer.current?.disconnect();
    };
  }, [messages, chatContainerRef.current, chatId, user, hasScrolled]);

  useEffect(() => {
    return () => {
      sendReadedDebounced.cancel();
    };
  }, [chatId]);
};
